import * as Zod from 'zod';

import { translate } from 'utils/i18n';
import {
    IExpiryScale,
    INotificationOffsetScale,
    INotificationTargets,
    ITemplate,
} from 'graphql/types';
import { EScoringSystemOption } from 'certificate/types';
import { colorValidator } from 'common/validators/color';
import { extraCategoryInputValidator } from 'common/validators/extraCategory';

export function getCertificateFormValidator() {
    return Zod.object({
        name: Zod.string(translate('validate.required')).nonempty(
            translate('validate.required')
        ),
        title: Zod.string(translate('validate.required')).nonempty(
            translate('validate.required')
        ),
        subtitle: Zod.string().optional().nullable(),
        text: Zod.string().optional().nullable(),
        primaryColor: colorValidator(),
        secondaryColor: colorValidator(),
        template: Zod.enum(ITemplate).optional().nullable(),
        customTemplateId: Zod.string().optional().nullable(),
        signatureFileId: Zod.string().optional().nullable(),
        showCourses: Zod.boolean().optional().nullable(),
        showCourseScore: Zod.boolean().optional().nullable(),
        showCourseTimeSpent: Zod.boolean().optional().nullable(),
        pageTwoText: Zod.string().optional().nullable(),
        expiry: Zod.coerce
            .number<number>(translate('validate.mustBeANumber'))
            .int(translate('validate.integer'))
            .nonnegative(translate('validate.zeroOrPositive'))
            .optional()
            .nullable(),
        expiryScale: Zod.enum(IExpiryScale).optional().nullable(),
        sendAchievedMail: Zod.boolean().optional().nullable(),
        sendNotification: Zod.boolean().optional().nullable(),
        notificationOffset: Zod.coerce
            .number<number>(translate('validate.mustBeANumber'))
            .int(translate('validate.integer'))
            .nonnegative(translate('validate.zeroOrPositive'))
            .optional()
            .nullable(),
        notificationOffsetScale: Zod.enum(INotificationOffsetScale)
            .optional()
            .nullable(),
        notificationTargets: Zod.array(Zod.enum(INotificationTargets))
            .transform((val) => (!val.length ? undefined : val))
            .optional()
            .nullable(),
        reference: Zod.string().optional().nullable(),
        scoring: Zod.object({
            score: Zod.coerce
                .number<number>(translate('validate.mustBeANumber'))
                .nonnegative(translate('validate.zeroOrPositive'))
                .optional()
                .nullable(),
            scoringSystemId: Zod.string().optional().nullable(),
        })
            .refine(
                (data) => {
                    // scoringSystemId is required only if score has a value
                    if (!!data.score) {
                        return (
                            !!data.scoringSystemId &&
                            data.scoringSystemId !==
                                EScoringSystemOption.NoScoringSystem
                        );
                    }

                    return true;
                },
                {
                    message: translate('validate.required'),
                    path: ['scoringSystemId'],
                }
            )
            // If score is not set, remove the scoring object
            .transform((val) => (!val.score ? undefined : val))
            .optional()
            .nullable(),
        extraFields: Zod.array(Zod.string()).optional().nullable(),
        extraCategories: extraCategoryInputValidator(true).optional(),
    })
        .refine(
            (data) => {
                // customTemplateId is required when template is Custom
                if (data.template === ITemplate.Custom) {
                    return !!data.customTemplateId;
                }

                return true;
            },
            {
                message: translate('validate.required'),
                path: ['customTemplateId'],
            }
        )
        .transform((data) => {
            // Clear customTemplateId when template is not Custom
            if (data.template !== ITemplate.Custom) {
                return {
                    ...data,
                    customTemplateId: null,
                };
            }

            return data;
        });
}
